import React, { useEffect, useMemo, useState } from 'react';
import { buildAdsDataWeeklyQuery, fetchData } from '../components/misc';

function formatDateForApi(value) {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return '';
  return `${day}-${month}-${year.slice(-2)}`;
}

function formatDateObjectForApi(dateValue) {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return '';
  }
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');
  return `${day}-${month}-${String(year).slice(-2)}`;
}

function getTodayInputDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toDateTimeLocalValue(dateValue) {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');
  const hours = String(dateValue.getHours()).padStart(2, '0');
  const minutes = String(dateValue.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}


function formatDateTimeWithTimezoneOffset(dateValue) {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return '';
  }

  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');
  const hours = String(dateValue.getHours()).padStart(2, '0');
  const minutes = String(dateValue.getMinutes()).padStart(2, '0');
  const seconds = String(dateValue.getSeconds()).padStart(2, '0');

  const offsetMinutes = -dateValue.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteOffset = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absoluteOffset / 60)).padStart(2, '0');
  const offsetMins = String(absoluteOffset % 60).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
}
function parseLocalDateTime(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
}

export default function AdsDataDashboard() {
  const defaultRangeEnd = useMemo(() => new Date(), []);
  const defaultRangeStart = useMemo(() => {
    const date = new Date(defaultRangeEnd);
    date.setDate(date.getDate() - 6);
    return date;
  }, [defaultRangeEnd]);
  const [selectedDate, setSelectedDate] = useState(getTodayInputDate());
  const [rangeStart, setRangeStart] = useState(toDateTimeLocalValue(defaultRangeStart));
  const [rangeEnd, setRangeEnd] = useState(toDateTimeLocalValue(defaultRangeEnd));
  const [counts, setCounts] = useState({ A: 0, B: 0, C: 0 });
  const [countsByBusiness, setCountsByBusiness] = useState({});
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [weeklyCounts, setWeeklyCounts] = useState({
    PH: { A: 0, B: 0, C: 0 },
    UH: { A: 0, B: 0, C: 0 },
  });
  const [weeklyError, setWeeklyError] = useState('');

  const businessKeys = useMemo(() => Object.keys(countsByBusiness), [countsByBusiness]);
  const activeCounts = selectedBusiness && countsByBusiness[selectedBusiness]
    ? countsByBusiness[selectedBusiness]
    : counts;

  const summaryText = useMemo(() => (
    `A: ${activeCounts.A ?? 0}\nB: ${activeCounts.B ?? 0}\nC: ${activeCounts.C ?? 0}`
  ), [activeCounts]);

  useEffect(() => {
    const controller = new AbortController();
    const apiDate = formatDateForApi(selectedDate);
    if (!apiDate) {
      setError('Invalid date selection.');
      return () => controller.abort();
    }

    setError('');
    fetchData(`/api/sirri_api/ads_data?date=${encodeURIComponent(apiDate)}`, { signal: controller.signal })
      .then((data) => {
        if (data && data.responsecode === 200) {
          setCounts(data.counts || { A: 0, B: 0, C: 0 });
          setCountsByBusiness(data.counts_by_business || {});
          const keys = Object.keys(data.counts_by_business || {});
          setSelectedBusiness((prev) => (prev && keys.includes(prev) ? prev : (keys[0] || '')));
          setStatus('');
        } else {
          setCounts({ A: 0, B: 0, C: 0 });
          setCountsByBusiness({});
          setSelectedBusiness('');
          setStatus('');
          setError(data?.message || 'Unable to load ads data.');
        }
      })
      .catch((err) => {
        if (err.name === 'CanceledError') return;
        setError('Unable to load ads data.');
        setCountsByBusiness({});
        setSelectedBusiness('');
      });

    return () => controller.abort();
  }, [selectedDate]);

  useEffect(() => {
    const controller = new AbortController();
    const startDateValue = parseLocalDateTime(rangeStart);
    const endDateValue = parseLocalDateTime(rangeEnd);

    if (!startDateValue || !endDateValue) {
      setWeeklyError('Select valid start and end date/time values.');
      return () => controller.abort();
    }

    if (startDateValue > endDateValue) {
      setWeeklyError('Start date/time must be on or before end date/time.');
      return () => controller.abort();
    }

    const startDatetimeApi = formatDateTimeWithTimezoneOffset(startDateValue);
    const endDatetimeApi = formatDateTimeWithTimezoneOffset(endDateValue);
    const startApi = formatDateObjectForApi(startDateValue);
    const endApi = formatDateObjectForApi(endDateValue);

    const params = buildAdsDataWeeklyQuery({
      start_datetime: startDatetimeApi,
      end_datetime: endDatetimeApi,
      start_date: startApi,
      end_date: endApi,
    });

    fetchData(
      `/api/sirri_api/ads_data_weekly?${params.toString()}`,
      { signal: controller.signal },
    )
      .then((data) => {
        if (data && data.responsecode === 200) {
          setWeeklyCounts(data.counts_by_business || {
            PH: { A: 0, B: 0, C: 0 },
            UH: { A: 0, B: 0, C: 0 },
          });
          setWeeklyError('');
        } else {
          setWeeklyError(data?.message || 'Unable to load weekly ads data.');
        }
      })
      .catch((err) => {
        if (err.name === 'CanceledError') return;
        setWeeklyError('Unable to load weekly ads data.');
      });

    return () => controller.abort();
  }, [rangeStart, rangeEnd]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setStatus('Copied to clipboard.');
    } catch (err) {
      setStatus('Unable to copy.');
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="bg-white text-black rounded shadow p-4">
        <h1 className="text-xl font-bold mb-2">Ads Inquiry Summary</h1>
        <p className="text-sm text-gray-600 mb-4">Select a day to aggregate inquiries.</p>
        <label className="block text-sm font-medium mb-2" htmlFor="ads-date">
          Date
        </label>
        <input
          id="ads-date"
          type="date"
          className="border rounded p-2 text-black"
          value={selectedDate}
          onChange={(event) => setSelectedDate(event.target.value)}
        />
      </div>

      <div className="bg-white text-black rounded shadow p-4 space-y-3">
        <h2 className="font-semibold">Summary</h2>
        <div>
          <label className="block text-sm font-medium mb-2" htmlFor="business-key">
            Business Key
          </label>
          <select
            id="business-key"
            className="border rounded p-2 text-black w-full"
            value={selectedBusiness}
            onChange={(event) => setSelectedBusiness(event.target.value)}
            disabled={!businessKeys.length}
          >
            {businessKeys.length ? (
              businessKeys.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))
            ) : (
              <option value="">No business keys</option>
            )}
          </select>
        </div>
        <textarea
          className="w-full border rounded p-2 text-black"
          rows={4}
          readOnly
          value={summaryText}
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="bg-primary text-white px-4 py-2 rounded"
            onClick={handleCopy}
          >
            Copy Summary
          </button>
          {status ? <span className="text-sm text-green-600">{status}</span> : null}
        </div>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </div>

      <div className="bg-white text-black rounded shadow p-4 space-y-3">
        <h2 className="font-semibold">Date Range (PH &amp; UH)</h2>
        <p className="text-sm text-gray-600">
          Stage totals aggregated between local date/time values (minute precision) and converted to UTC for the API.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm font-medium" htmlFor="ads-range-start">
            Begin date/time (local timezone)
            <input
              id="ads-range-start"
              type="datetime-local"
              className="mt-1 w-full border rounded p-2 text-black"
              value={rangeStart}
              onChange={(event) => setRangeStart(event.target.value)}
            />
          </label>
          <label className="block text-sm font-medium" htmlFor="ads-range-end">
            End date/time (local timezone)
            <input
              id="ads-range-end"
              type="datetime-local"
              className="mt-1 w-full border rounded p-2 text-black"
              value={rangeEnd}
              onChange={(event) => setRangeEnd(event.target.value)}
            />
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full border text-sm">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="border px-3 py-2">Business Key</th>
                <th className="border px-3 py-2">Stage A</th>
                <th className="border px-3 py-2">Stage B</th>
                <th className="border px-3 py-2">Stage C</th>
              </tr>
            </thead>
            <tbody>
              {['PH', 'UH'].map((key) => (
                <tr key={key}>
                  <td className="border px-3 py-2 font-medium">{key}</td>
                  <td className="border px-3 py-2">{weeklyCounts[key]?.A ?? 0}</td>
                  <td className="border px-3 py-2">{weeklyCounts[key]?.B ?? 0}</td>
                  <td className="border px-3 py-2">{weeklyCounts[key]?.C ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {weeklyError ? <p className="text-sm text-red-600">{weeklyError}</p> : null}
      </div>
    </div>
  );
}
